// src/utils/exportUsersToExcel.ts
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { RootState } from '../store/store';
import { getCsrfToken } from './helpers';

type UserForExport = RootState['adminUsers']['users'][0];

// Локально расширяем тип пользователя теми полями, которые реально приходят с бэка,
// но не описаны в UserData.
type ExtendedUserForExport = UserForExport & {
    address?: {
        raw?: string;
        // Остальная структура адреса нам тут не важна, поэтому
        // не расписываем все поля, но и any не используем.
        [key: string]: unknown;
    } | null;
    lastAuthDate?: string | null;
};

// Минимально необходимый тип заказа для статистики
type OrderForStats = RootState['common']['orders'][0] & {
    user?: {
        id?: string;
        [key: string]: unknown;
    };
    userID?: string;
    orderStatus?: string;
    totalDiscount?: number;
    fundsSpent?: number;
};

type UserOrderStats = {
    totalOrders: number;
    discountedOrders: number;
    totalSpent: number;
};

type UserCurrentState = {
    cartCount: number;
    favoritesCount: number;
};

type CartFavoritesDTO = {
    userId: string;
    cartCount: number;
    favoritesCount: number;
};

const getTierName = (tier: string | undefined | null): string => {
    const names: Record<string, string> = {
        base: 'бронзовый',
        silver: 'серебряный',
        gold: 'золотой',
        platinum: 'VIP',
    };
    if (!tier) return '';
    return names[tier] || tier;
};

const getStatusName = (status: string | undefined | null): string => {
    if (!status) return '';
    if (status === 'employee') return 'сотрудник';
    if (status === 'customer') return 'покупатель';
    return status;
};

// Завершённым считаем только orderStatus === "completed"
const isCompletedOrder = (order: OrderForStats): boolean => order.orderStatus === 'completed';

// Заказ со скидкой: totalDiscount !== 0
const hasDiscountInOrder = (order: OrderForStats): boolean => {
    const discountValue = typeof order.totalDiscount === 'number' ? order.totalDiscount : 0;
    return discountValue !== 0;
};

// Строим карту статистики по пользователям (завершённые заказы)
const buildUserStatsMap = (orders: OrderForStats[]): Map<string, UserOrderStats> => {
    const stats = new Map<string, UserOrderStats>();

    orders.forEach((order) => {
        if (!isCompletedOrder(order)) return;

        const userId = order.user?.id ?? order.userID;
        if (!userId) return;

        const current: UserOrderStats = stats.get(userId) ?? {
            totalOrders: 0,
            discountedOrders: 0,
            totalSpent: 0,
        };

        current.totalOrders += 1;

        if (hasDiscountInOrder(order)) {
            current.discountedOrders += 1;
        }

        const spent = typeof order.fundsSpent === 'number' ? order.fundsSpent : 0;
        current.totalSpent += spent;

        stats.set(userId, current);
    });

    return stats;
};

// Батч-получение текущего состояния корзины и избранного по userId[]
async function fetchCartAndFavoritesCounts(userIds: string[]): Promise<Map<string, UserCurrentState>> {
    const map = new Map<string, UserCurrentState>();
    const uniqueIds = Array.from(new Set(userIds.filter((id) => !!id)));

    if (!uniqueIds.length) return map;

    const csrfToken = await getCsrfToken();
    const baseUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

    // Ожидаемый бэкенд-роут: POST /user/cart-favorites-counts
    const response = await axios.post<CartFavoritesDTO[]>(
        `${baseUrl}/user/cart-favorites-counts`,
        { ids: uniqueIds },
        {
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            withCredentials: true,
        },
    );

    response.data.forEach((item) => {
        const cartCount = Number.isFinite(Number(item.cartCount)) ? Number(item.cartCount) : 0;
        const favoritesCount = Number.isFinite(Number(item.favoritesCount)) ? Number(item.favoritesCount) : 0;
        map.set(item.userId, { cartCount, favoritesCount });
    });

    return map;
}

/* ---------- Оверлоады для обратной совместимости ---------- */

// как раньше: только пользователи, без статистики по заказам
export async function exportUsersToExcel(users: ExtendedUserForExport[], fileName?: string): Promise<void>;

// новый вариант: пользователи + заказы + опциональное имя файла
export async function exportUsersToExcel(users: ExtendedUserForExport[], orders: OrderForStats[], fileName?: string): Promise<void>;

// реализация
export async function exportUsersToExcel(users: ExtendedUserForExport[], second?: string | OrderForStats[], third?: string): Promise<void> {
    let orders: OrderForStats[] = [];
    let fileName = 'Список_пользователей.xlsx';

    if (Array.isArray(second)) {
        orders = second;
        if (typeof third === 'string') {
            fileName = third;
        }
    } else if (typeof second === 'string') {
        fileName = second;
    }

    /* ---------- 0. Статистика по завершённым заказам ---------- */
    const userStatsMap = buildUserStatsMap(orders);

    /* ---------- 0.1. Текущее состояние корзины и избранного ---------- */
    let currentStateMap = new Map<string, UserCurrentState>();
    try {
        const ids = users.map((u) => u.id).filter((id): id is string => typeof id === 'string' && !!id);
        currentStateMap = await fetchCartAndFavoritesCounts(ids);
    } catch (err) {
        // В случае ошибки просто оставляем карту пустой,
        // чтобы выгрузка не падала.
        console.error('Failed to fetch cart/favorites counts for export:', err);
    }

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Пользователи');

    /* ---------- 1. Пустая строка ---------- */
    ws.addRow([]);

    /* ---------- 2. Заголовок ---------- */
    const titleRow = ws.addRow(['Список пользователей']);
    titleRow.font = { bold: true, size: 14 };
    titleRow.alignment = { horizontal: 'center' };
    // Теперь у нас 14 колонок: A-N
    ws.mergeCells('A2:N2');

    /* ---------- 3. Пустая строка ---------- */
    ws.addRow([]);

    /* ---------- 4. Настройка колонок ---------- */
    ws.columns = [
        { key: 'id', width: 20 }, // A — ID пользователя
        { key: 'fullName', width: 32 }, // B — ФИО
        { key: 'dateOfBirth', width: 16 }, // C — Дата рождения
        { key: 'createdAt', width: 18 }, // D — Дата регистрации
        { key: 'phoneNumber', width: 16 }, // E — Телефон
        { key: 'email', width: 36 }, // F — E-mail
        { key: 'cardNumber', width: 20 }, // G — Номер карты
        { key: 'tier', width: 20 }, // H — Уровень карты (x%)
        { key: 'balance', width: 16 }, // I — Баланс баллов
        { key: 'status', width: 18 }, // J — Статус клиента
        { key: 'address', width: 40 }, // K — Адрес доставки
        { key: 'lastAuth', width: 22 }, // L — Последняя авторизация
        { key: 'currentState', width: 26 }, // M — Текущее состояние (Корзина/Избранное)
        { key: 'ordersStats', width: 32 }, // N — Завершенные заказы
    ];

    const headerTitles = [
        'ID пользователя', // A
        'ФИО', // B
        'Дата рождения', // C
        'Дата регистрации', // D
        'Телефон', // E
        'E-mail', // F
        'Номер карты', // G
        'Уровень карты', // H
        'Баланс баллов', // I
        'Статус клиента', // J
        'Адрес доставки', // K
        'Последняя авторизация', // L
        'Текущее состояние', // M — Корзина / Избранное
        'Завершенные заказы', // N — всего / со скидкой / сумма
    ];

    const headerRow = ws.addRow(headerTitles);
    headerRow.font = { bold: true };

    // Лёгкое оформление шапки таблицы
    headerRow.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE6E6E6' },
        };
        cell.border = {
            top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
            left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
            bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } },
            right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });

    // Центрируем "служебные" и числовые колонки
    // A: ID, C: дата рождения, D: дата регистрации, E: телефон,
    // G: номер карты, H: уровень карты, I: баланс, J: статус,
    // L: последняя авторизация, M: текущее состояние, N: завершённые заказы
    ['A', 'C', 'D', 'E', 'G', 'H', 'I', 'J', 'L', 'M', 'N'].forEach((col) => {
        const column = ws.getColumn(col);
        column.alignment = {
            ...(column.alignment ?? {}),
            horizontal: 'center',
        };
    });

    // Вертикальное выравнивание и перенос текста для всех колонок
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'].forEach((col) => {
        const column = ws.getColumn(col);
        column.alignment = {
            ...(column.alignment ?? {}),
            vertical: 'middle',
            wrapText: true,
        };
    });

    /* ---------- 5. Данные ---------- */
    users.forEach((user, index) => {
        const fullName = `${user.lastName ?? ''} ${user.firstName ?? ''} ${user.middleName ?? ''}`.trim() || '—';

        // Дата рождения: "27.11.1984 от 31.10.2025"
        const dobPart = user.dateOfBirth?.date ?? '';
        const dobUpdatedPart = user.dateOfBirth?.updatedAt ? ` от ${new Date(user.dateOfBirth.updatedAt).toLocaleDateString('ru-RU')}` : '';
        const dateOfBirth = `${dobPart}${dobUpdatedPart}`;

        const createdAt = user.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : '';

        // E-mail + пометка, если не подтверждён
        const baseEmail = user.email ?? '';
        const email = baseEmail ? (user.emailStatus ? baseEmail : `${baseEmail} (не подтвержден)`) : '';

        const cardNumber = user.card?.cardNumber ?? '';
        const tier = getTierName(user.card?.level);
        const discount = user.card?.discount ?? 0;
        const balance = user.card?.balance ?? 0;

        // Уровень карты + "(x%)"
        const discountText = typeof discount === 'number' ? `${discount}%` : '';
        let tierWithDiscount = tier;
        if (tier && discountText) {
            tierWithDiscount = `${tier} (${discountText})`;
        } else if (!tier && discountText) {
            tierWithDiscount = `(${discountText})`;
        }

        const status = getStatusName(user.status as string);

        const address = user.address?.raw ?? '';

        const lastAuth = user.lastAuthDate ? new Date(user.lastAuthDate).toLocaleString('ru-RU').replace(',', '') : '';

        const stats = userStatsMap.get(user.id) ?? {
            totalOrders: 0,
            discountedOrders: 0,
            totalSpent: 0,
        };

        const orderStatsCellValue =
            stats.totalOrders === 0 ? '—' : `Всего: ${stats.totalOrders}; со скидкой: ${stats.discountedOrders}; сумма: ${stats.totalSpent.toFixed(2)} ₽`;

        const currentState = user.id ? (currentStateMap.get(user.id) ?? { cartCount: 0, favoritesCount: 0 }) : { cartCount: 0, favoritesCount: 0 };

        const currentStateCellValue = `Корзина: ${currentState.cartCount}, Избранное: ${currentState.favoritesCount}`;

        const row = ws.addRow([
            user.id ?? '', // A
            fullName, // B
            dateOfBirth, // C
            createdAt, // D
            user.phoneNumber ?? '', // E
            email, // F
            cardNumber, // G
            tierWithDiscount, // H
            balance, // I
            status, // J
            address, // K
            lastAuth, // L
            currentStateCellValue, // M
            orderStatsCellValue, // N
        ]);

        // Лёгкая разделительная сетка + "зебра"
        row.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFEFEFEF' } },
                left: { style: 'thin', color: { argb: 'FFEFEFEF' } },
                bottom: { style: 'thin', color: { argb: 'FFEFEFEF' } },
                right: { style: 'thin', color: { argb: 'FFEFEFEF' } },
            };
        });

        if (index % 2 === 1) {
            row.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF9F9F9' },
                };
            });
        }
    });

    /* ---------- 6. Автофильтр (сортировка/фильтрация в Excel) ---------- */
    const lastDataRow = 4 + users.length; // 4 = строки до заголовков включительно
    ws.autoFilter = { from: 'A4', to: `N${lastDataRow}` };

    /* ---------- 7. Пустая строка после таблицы ---------- */
    ws.addRow([]);

    /* ---------- 8. Строка даты выгрузки ---------- */
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const dateRow = ws.addRow([`Дата выгрузки: ${dd}.${mm}.${yyyy}`]);
    const dateRowNum = dateRow.number;
    ws.mergeCells(`A${dateRowNum}:N${dateRowNum}`);
    dateRow.alignment = { horizontal: 'right' };

    /* ---------- 9. Заморозка шапки ---------- */
    ws.views = [{ state: 'frozen', ySplit: 4 }];

    /* ---------- 10. Выгрузка файла ---------- */
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
}
